import React from "react";
import styled from "styled-components";
import { Button, message } from "antd";
import { Printer } from "@phosphor-icons/react";
import { useMutation } from "react-query";
import { useTranslation } from "react-i18next";
import { useBranchContext } from "../../../contexts/branch-context";
import { testPrinter, TestPrinterResult } from "../../../service/client";
import { colors, spacing } from "../../../styles/theme";

const Wrapper = styled.div`
  padding: ${spacing.md};
  background: ${colors.white};
  border-radius: 8px;
`;

const Header = styled.div`
  margin-bottom: ${spacing.lg};
`;

const Title = styled.h2`
  font-size: 18px;
  font-weight: 600;
  margin: 0;
  color: ${colors.textPrimary};
`;

const Description = styled.p`
  color: ${colors.textSecondary};
  margin: 4px 0 0;
  font-size: 14px;
`;

const Card = styled.div`
  border: 1px solid ${colors.borderLight};
  border-radius: 8px;
  padding: ${spacing.lg};
  display: flex;
  flex-direction: column;
  gap: ${spacing.md};
  max-width: 520px;
`;

const Hint = styled.p`
  color: ${colors.textSecondary};
  font-size: 13px;
  margin: 0;
`;

export const PrinterSettings: React.FC = () => {
  const { t } = useTranslation();
  const { currentBranch } = useBranchContext();

  const testMutation = useMutation(
    () => testPrinter(currentBranch!.id),
    {
      onSuccess: (data: TestPrinterResult) => {
        if (data.ok) {
          message.success(data.message || t("printer.success"));
        } else {
          message.error(data.error || t("printer.errorGeneric"));
        }
      },
      onError: (error: any) => {
        message.error(
          error?.response?.data?.error || t("printer.errorConnect")
        );
      },
    }
  );

  return (
    <Wrapper>
      <Header>
        <Title>{t("printer.title")}</Title>
        <Description>{t("printer.description")}</Description>
      </Header>

      <Card>
        <Button
          type="primary"
          icon={<Printer size={18} weight="regular" />}
          loading={testMutation.isLoading}
          disabled={!currentBranch}
          onClick={() => testMutation.mutate()}
          style={{ alignSelf: "flex-start", display: "flex", alignItems: "center", gap: 8 }}
        >
          {t("printer.testButton")}
        </Button>
        <Hint>{t("printer.hint")}</Hint>
      </Card>
    </Wrapper>
  );
};

export default PrinterSettings;
